import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { naturePivotPage_Query } from "@app/queries/naturePivotPage_Query.graphql";
import type { naturePivotPage_CreateMutation } from "@app/queries/naturePivotPage_CreateMutation.graphql";
import type { naturePivotPage_UpdateMutation } from "@app/queries/naturePivotPage_UpdateMutation.graphql";
import type { naturePivotPage_DeleteMutation } from "@app/queries/naturePivotPage_DeleteMutation.graphql";

export const naturePivotQuery = graphql`
  query naturePivotPage_Query($perspectiveId: ID) {
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
      parent {
        id
      }
    }
    natureCrossTab(perspectiveId: $perspectiveId) {
      totalCents
      byStakeholder {
        cents
        expenseNature {
          id
          name
        }
        stakeholder {
          id
          name
        }
      }
      byPnlLine {
        cents
        pnlLine
        expenseNature {
          id
          name
        }
      }
    }
  }
`;

const createMutation = graphql`
  mutation naturePivotPage_CreateMutation($input: CreateExpenseNatureInputObject!) {
    createExpenseNature(input: $input) {
      expenseNature { id name }
      errors { message }
    }
  }
`;

const updateMutation = graphql`
  mutation naturePivotPage_UpdateMutation($input: UpdateExpenseNatureInputObject!) {
    updateExpenseNature(input: $input) {
      expenseNature { id name }
      errors { message }
    }
  }
`;

const deleteMutation = graphql`
  mutation naturePivotPage_DeleteMutation($input: DeleteExpenseNatureInputObject!) {
    deleteExpenseNature(input: $input) {
      expenseNature { id }
      errors { message }
    }
  }
`;

type Axis = "stakeholder" | "pnlLine";
type Tab = "pivot" | "manage";
const UNCLASSIFIED = "Unclassified";

// The subset of the /transactions search params a pivot cell drills into.
type TxnDrillSearch = {
  nature?: string;
  counterparty?: string;
  pnlLine?:
    | "REVENUE"
    | "COGS"
    | "S_AND_M"
    | "R_AND_D"
    | "G_AND_A"
    | "UNCATEGORIZED"
    | "TAXES";
};

type PageData = naturePivotPage_Query["response"];
type NatureNode = PageData["expenseNatures"][number];

function formatMoney(cents: number): string {
  if (cents === 0) return "—";
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  const text =
    abs >= 1_000_000
      ? `${(abs / 1_000_000).toFixed(2)}M`
      : abs >= 1_000
      ? `${(abs / 1_000).toFixed(1)}k`
      : abs.toFixed(0);
  return `${dollars < 0 ? "-" : ""}$${text}`;
}

export default function NaturePivotPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<naturePivotPage_Query>;
}) {
  const preloaded = usePreloadedQuery(naturePivotQuery, queryRef);
  const [data, setData] = useState<PageData>(preloaded);
  useEffect(() => setData(preloaded), [preloaded]);
  const [tab, setTab] = useState<Tab>("pivot");

  const refreshInPlace = () => {
    fetchQuery<naturePivotPage_Query>(environment, naturePivotQuery, {
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((fresh) => fresh && setData(fresh));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Expense nature</h1>
        <div className="flex gap-1 text-sm">
          {(["pivot", "manage"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-3 py-1 rounded border ${tab === t ? "border-gray-800 bg-gray-800 text-white" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              {t === "pivot" ? "Spend by nature" : "Manage natures"}
            </button>
          ))}
        </div>
      </div>

      {tab === "pivot" ? (
        <Pivot cube={data.natureCrossTab} />
      ) : (
        <TreeManager natures={data.expenseNatures} onChanged={refreshInPlace} />
      )}
    </div>
  );
}

// ---- Pivot ----------------------------------------------------------------

type Cell = { natureName: string; colName: string; cents: number };

function Pivot({ cube }: { cube: PageData["natureCrossTab"] }) {
  const [axis, setAxis] = useState<Axis>("pnlLine");
  const navigate = useNavigate();

  // Maps for the cell drill-down: nature name -> its GID; (stakeholder axis) column
  // name -> the stakeholder GID. The pnl axis uses the column name (the P&L line) directly.
  const natureIdByName = new Map<string, string | null>();
  const stakeholderIdByName = new Map<string, string | null>();
  const cells: Cell[] =
    axis === "stakeholder"
      ? cube.byStakeholder.map((c) => {
          const natureName = c.expenseNature?.name ?? UNCLASSIFIED;
          const colName = c.stakeholder?.name ?? "(no counterparty)";
          natureIdByName.set(natureName, c.expenseNature?.id ?? null);
          stakeholderIdByName.set(colName, c.stakeholder?.id ?? null);
          return { natureName, colName, cents: c.cents };
        })
      : cube.byPnlLine.map((c) => {
          const natureName = c.expenseNature?.name ?? UNCLASSIFIED;
          natureIdByName.set(natureName, c.expenseNature?.id ?? null);
          return { natureName, colName: c.pnlLine, cents: c.cents };
        });

  // Open the transactions behind a cell (or a whole nature row when colName is null),
  // filtered by nature + the column key. Unclassified (null nature) drills by column only.
  const drill = (natureName: string, colName: string | null) => {
    const natureId = natureIdByName.get(natureName) ?? null;
    const search: TxnDrillSearch = {};
    if (natureId) search.nature = natureId;
    if (colName !== null) {
      if (axis === "stakeholder") {
        const sid = stakeholderIdByName.get(colName) ?? null;
        if (sid) search.counterparty = sid;
      } else {
        search.pnlLine = colName as TxnDrillSearch["pnlLine"];
      }
    }
    if (Object.keys(search).length === 0) return;
    navigate({ to: "/transactions", search });
  };

  const matrix = new Map<string, Map<string, number>>();
  const rowTotals = new Map<string, number>();
  const colTotals = new Map<string, number>();
  for (const cell of cells) {
    if (!matrix.has(cell.natureName)) matrix.set(cell.natureName, new Map());
    const row = matrix.get(cell.natureName)!;
    row.set(cell.colName, (row.get(cell.colName) ?? 0) + cell.cents);
    rowTotals.set(cell.natureName, (rowTotals.get(cell.natureName) ?? 0) + cell.cents);
    colTotals.set(cell.colName, (colTotals.get(cell.colName) ?? 0) + cell.cents);
  }
  const rows = [...rowTotals.keys()].sort((a, b) => (rowTotals.get(b) ?? 0) - (rowTotals.get(a) ?? 0));
  const cols = [...colTotals.keys()].sort((a, b) => (colTotals.get(b) ?? 0) - (colTotals.get(a) ?? 0));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm text-gray-500">
          Operating cost sliced by expense nature (cash basis). Reconciles to the cash P&amp;L:{" "}
          <span className="font-medium text-gray-700">{formatMoney(cube.totalCents)}</span>.
        </p>
        <div className="flex gap-1 text-sm">
          <button type="button" onClick={() => setAxis("pnlLine")} className={`px-2 py-1 rounded border ${axis === "pnlLine" ? "border-gray-800 bg-gray-800 text-white" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>× P&amp;L line</button>
          <button type="button" onClick={() => setAxis("stakeholder")} className={`px-2 py-1 rounded border ${axis === "stakeholder" ? "border-gray-800 bg-gray-800 text-white" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>× Stakeholder</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-lg bg-white p-10 text-center mt-3">
          <p className="text-sm text-gray-500">No operating cost to slice yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white mt-3">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500">
                <th className="text-left font-medium px-3 py-2 sticky left-0 bg-white">Nature</th>
                {cols.map((col) => (
                  <th key={col} className="text-right font-medium px-3 py-2 whitespace-nowrap">{col}</th>
                ))}
                <th className="text-right font-semibold px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row} className="border-b border-gray-100 hover:bg-gray-50">
                  <td
                    className="text-left px-3 py-2 font-medium sticky left-0 bg-white cursor-pointer hover:text-indigo-700"
                    title="Show all transactions of this nature"
                    onClick={() => drill(row, null)}
                  >
                    {row}
                  </td>
                  {cols.map((col) => {
                    const cents = matrix.get(row)?.get(col) ?? 0;
                    return (
                      <td
                        key={col}
                        className={`text-right px-3 py-2 tabular-nums text-gray-700 ${cents !== 0 ? "cursor-pointer hover:text-indigo-700 hover:underline" : ""}`}
                        title={cents !== 0 ? "Show the transactions behind this cell" : undefined}
                        onClick={cents !== 0 ? () => drill(row, col) : undefined}
                      >
                        {formatMoney(cents)}
                      </td>
                    );
                  })}
                  <td className="text-right px-3 py-2 tabular-nums font-semibold">{formatMoney(rowTotals.get(row) ?? 0)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 font-semibold">
                <td className="text-left px-3 py-2 sticky left-0 bg-white">Total</td>
                {cols.map((col) => (
                  <td key={col} className="text-right px-3 py-2 tabular-nums">{formatMoney(colTotals.get(col) ?? 0)}</td>
                ))}
                <td className="text-right px-3 py-2 tabular-nums">{formatMoney(cube.totalCents)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Tree manager ---------------------------------------------------------

function TreeManager({ natures, onChanged }: { natures: ReadonlyArray<NatureNode>; onChanged: () => void }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const childrenOf = (parentId: string | null): ReadonlyArray<NatureNode> =>
    natures.filter((n) => (n.parent?.id ?? null) === parentId && n.name !== "#nature-root");
  // A seeded "#nature-root" parents the top level; surface its children as roots too.
  const seededRoot = natures.find((n) => n.name === "#nature-root");
  const roots = childrenOf(null).concat(seededRoot ? childrenOf(seededRoot.id) : []);

  const finish = (payload: { errors: ReadonlyArray<{ message: string }> } | null | undefined) => {
    setBusy(false);
    if (payload?.errors.length) setError(payload.errors[0].message);
    else onChanged();
  };

  const create = (name: string, parentId?: string) => {
    const perspectiveId = getPerspectiveId();
    if (!perspectiveId) { setError("Select a perspective first."); return; }
    setBusy(true);
    setError(null);
    commitMutation<naturePivotPage_CreateMutation>(environment, {
      mutation: createMutation,
      variables: { input: { perspectiveId, name, parentId } },
      onCompleted: (res) => finish(res.createExpenseNature),
      onError: (e) => { setBusy(false); setError(e.message); },
    });
  };
  const rename = (id: string, name: string) => {
    setBusy(true);
    setError(null);
    commitMutation<naturePivotPage_UpdateMutation>(environment, {
      mutation: updateMutation,
      variables: { input: { expenseNatureId: id, name } },
      onCompleted: (res) => finish(res.updateExpenseNature),
      onError: (e) => { setBusy(false); setError(e.message); },
    });
  };
  const remove = (id: string) => {
    setBusy(true);
    setError(null);
    commitMutation<naturePivotPage_DeleteMutation>(environment, {
      mutation: deleteMutation,
      variables: { input: { expenseNatureId: id } },
      onCompleted: (res) => finish(res.deleteExpenseNature),
      onError: (e) => { setBusy(false); setError(e.message); },
    });
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-gray-500 mb-3">
        The expense-nature taxonomy. Rename a node inline; delete re-parents its children and clears the tag on any tagged transaction.
      </p>
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

      <div className="flex gap-2 mb-4">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New top-level nature"
          className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
        />
        <button
          type="button"
          disabled={busy || newName.trim() === ""}
          onClick={() => { create(newName.trim()); setNewName(""); }}
          className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white disabled:opacity-40"
        >
          Add
        </button>
      </div>

      <ul className="border border-gray-200 rounded-lg bg-white divide-y divide-gray-100">
        {roots.length === 0 && <li className="p-4 text-sm text-gray-400">No natures yet. Add one above or run the seed task.</li>}
        {roots.map((node) => (
          <NatureRow key={node.id} node={node} depth={0} childrenOf={childrenOf} onRename={rename} onDelete={remove} onAddChild={create} busy={busy} />
        ))}
      </ul>
    </div>
  );
}

function NatureRow({
  node, depth, childrenOf, onRename, onDelete, onAddChild, busy,
}: {
  node: NatureNode;
  depth: number;
  childrenOf: (parentId: string | null) => ReadonlyArray<NatureNode>;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onAddChild: (name: string, parentId: string) => void;
  busy: boolean;
}) {
  const [name, setName] = useState(node.name);
  const [adding, setAdding] = useState("");
  const children = childrenOf(node.id);

  return (
    <>
      <li className="flex items-center gap-2 px-3 py-1.5" style={{ paddingLeft: 12 + depth * 20 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name !== node.name && onRename(node.id, name.trim())}
          className="flex-1 text-sm bg-transparent border-b border-transparent hover:border-gray-200 focus:border-gray-400 outline-none"
        />
        {depth < 2 && (
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && adding.trim()) { onAddChild(adding.trim(), node.id); setAdding(""); } }}
            placeholder="+ child"
            className="w-24 text-xs rounded border border-gray-200 px-1.5 py-0.5"
          />
        )}
        <button type="button" disabled={busy} onClick={() => onDelete(node.id)} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Delete</button>
      </li>
      {children.map((child) => (
        <NatureRow key={child.id} node={child} depth={depth + 1} childrenOf={childrenOf} onRename={onRename} onDelete={onDelete} onAddChild={onAddChild} busy={busy} />
      ))}
    </>
  );
}
